import { getPoolActiveStatusChip } from "~/utils/league";
import { Link } from "@remix-run/react";
import { formatAsCurrency } from "~/utils/number";

import { Button } from "~/components/ui/button";
import ClientCountdown from "~/components/ClientCountdown";
import { Loader2, Users, DollarSign, Trophy, Clock } from "lucide-react";
import GreenChip from "~/components/chip/GreenChip";

import { LegitAppCard } from "~/components/LegitAppCard";
import { PublicLeagueBettingPoolWithBetCount } from "~/services/league/leagueTypes";

interface Props {
  league: PublicLeagueBettingPoolWithBetCount;
  buttonState: "loading" | "leaderboard" | "join";
}

export default function PublicLeagueCardView({ buttonState, league }: Props) {
  function getButton() {
    if (buttonState === "loading") {
      return (
        <Button disabled className="w-full bg-muted text-muted-foreground">
          <Loader2 className="animate-spin w-4 h-4 mr-2" />
          Please wait
        </Button>
      );
    } else if (buttonState === "leaderboard") {
      return (
        <Button
          className="w-full bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
          size="lg"
        >
          <Trophy className="w-4 h-4 mr-2" />
          View Leaderboard
        </Button>
      );
    } else if (buttonState === "join") {
      return (
        <Button
          className="w-full bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
          size="lg"
        >
          Join League
        </Button>
      );
    }
  }

  function getLinkTo() {
    if (buttonState === "leaderboard") {
      return `/leaderboard/league/${league.id}`;
    } else if (buttonState === "join") {
      return `/join/parlay/${league.id}`;
    }

    return "/";
  }

  const hasEntryCost = league.entryCostCents && league.entryCostCents > 0n;
  const entryCostFormatted = hasEntryCost
    ? formatAsCurrency(Number(league.entryCostCents) / 100)
    : "Free";

  return (
    <LegitAppCard imageUrl={league.image_url ?? ""} title={league.label}>
      {/* Status and Countdown Row */}
      <div className="flex items-center justify-between mb-4">
        {getPoolActiveStatusChip(league)}
        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          <Clock className="w-3 h-3" />
          <ClientCountdown
            timestamp={league.closing_date_time ?? new Date()}
            countdownPrefix={""}
          />
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="flex items-center gap-2 p-3 bg-background/50 rounded-lg border border-border/30">
          <div className="p-1.5 bg-primary/10 rounded-md">
            <Users className="w-4 h-4 text-primary" />
          </div>
          <div>
            <div className="text-lg font-bold text-foreground">
              {league._count.bets}
            </div>
            <div className="text-xs text-muted-foreground">Players</div>
          </div>
        </div>

        <div className="flex items-center gap-2 p-3 bg-background/50 rounded-lg border border-border/30">
          <div className="p-1.5 bg-primary/10 rounded-md">
            <DollarSign className="w-4 h-4 text-primary" />
          </div>
          <div>
            <div className="text-lg font-bold text-foreground">
              {hasEntryCost ? entryCostFormatted : "Free"}
            </div>
            <div className="text-xs text-muted-foreground">Entry</div>
          </div>
        </div>
      </div>

      {/* Prize Information */}
      <div className="mb-6 p-4 bg-gradient-to-r from-primary/5 to-primary/10 rounded-xl border border-primary/20">
        <div className="flex items-center gap-2 mb-2">
          <Trophy className="w-4 h-4 text-primary" />
          <h4 className="text-sm font-semibold text-foreground">Prize Pool</h4>
        </div>
        <p className="text-sm text-muted-foreground leading-relaxed">
          {league.prize_explainer}
        </p>
      </div>

      {/* Action Button */}
      <div className="mt-auto">
        <Link to={getLinkTo()} prefetch="viewport" className="block">
          {getButton()}
        </Link>
      </div>
    </LegitAppCard>
  );
}
